import React, { useState, useEffect } from 'react';
import { BrowserRouter, Routes, Route, Navigate } from 'react-router-dom';
import { AppProvider, useApp } from './context/AppContext';

import Sidebar from './components/layout/Sidebar';
import Header from './components/layout/Header';
import GlobalBusy from './components/GlobalBusy';
import AsistenteIA from './components/AsistenteIA';
import OnboardingTour from './components/OnboardingTour';
import { LogoMarca, NombreMarca } from './components/Marca';

import Login from './pages/Login';
import CambioObligatorio from './pages/CambioObligatorio';
import Dashboard from './pages/dashboard/Dashboard';
import Clientes from './pages/clientes/Clientes';
import Polizas from './pages/polizas/Polizas';
import Pagos from './pages/pagos/Pagos';
import Comisiones from './pages/comisiones/Comisiones';
import CRM from './pages/crm/CRM';
import Reclamos from './pages/reclamos/Reclamos';
import Agentes from './pages/agentes/Agentes';
import Archivos from './pages/archivos/Archivos';
import Admin from './pages/admin/Admin';
import Solicitudes from './pages/solicitudes/Solicitudes';
import Cobros from './pages/cobros/Cobros';
import Contabilidad from './pages/contabilidad/Contabilidad';
import Reportes from './pages/reportes/Reportes';
import { LicenciaAviso, LicenciaBloqueada } from './components/LicenciaAviso';

const PAGE_TITLES = {
  '/':            'Dashboard',
  '/clientes':    'Clientes',
  '/polizas':     'Pólizas',
  '/solicitudes': 'Solicitudes',
  '/pagos':       'Pagos',
  '/cobros':      'Cobros',
  '/comisiones':  'Comisiones',
  '/contabilidad':'Contabilidad',
  '/crm':         'CRM de Gestión',
  '/reclamos':    'Reclamos',
  '/agentes':     'Agentes',
  '/archivos':    'Archivos',
  '/reportes':    'Reportes',
  '/admin':       'Administración',
};

// Qué gana el corredor con cada módulo que NO está en su plan. Se usa para
// invitarlo a ampliarlo en vez de solo cerrarle la puerta: el mensaje es
// "mira lo que te falta aprovechar", no "pagá más".
const MODULO_BENEFICIO = {
  cobros:       { titulo: 'Cobros y saco',        texto: 'Aplica los pagos de tus clientes en orden de vencimiento, controla la morosidad y recupera comisión que hoy se te escapa.' },
  comisiones:   { titulo: 'Comisiones',           texto: 'Sigue lo que te deben las aseguradoras, registra lo que te pagan y liquida a tus agentes sin cuadrar nada a mano.' },
  reclamos:     { titulo: 'Reclamos',             texto: 'Lleva cada reclamo con su ajustador, coberturas afectadas y documentos, para responderle al cliente con todo a la mano.' },
  contabilidad: { titulo: 'Contabilidad del corretaje', texto: 'Ingresos y egresos, ITBMS/DGI, estado de resultados y cuentas por cobrar y pagar de tu corretaje.' },
  agentes:      { titulo: 'Multi-agente',         texto: 'Maneja sub-agentes, sus carteras y la liquidación de sus comisiones.' },
  reportes:     { titulo: 'Reportes ejecutivos',  texto: 'Producción, cartera y cobros en reportes listos para exportar y presentar.' },
};

function ProtectedRoute({ children, modulo }) {
  const { user, loading, canView, moduloEnPlan, planNombre } = useApp();
  if (loading) return null;
  if (!user) return <Navigate to="/login" replace />;

  // El módulo no está incluido en el plan contratado → invitación a ampliarlo.
  if (modulo && !moduloEnPlan(modulo)) {
    const b = MODULO_BENEFICIO[modulo] || { titulo: 'Este módulo', texto: 'Está disponible en un plan superior.' };
    return (
      <div style={{ maxWidth: 560, margin: '48px auto', textAlign: 'center', color: 'var(--silver)' }}>
        <div style={{ fontSize: 44, marginBottom: 14 }}>✨</div>
        <div style={{ fontSize: 22, fontWeight: 800, color: 'var(--text)', marginBottom: 10 }}>{b.titulo}</div>
        <div style={{ fontSize: 14, lineHeight: 1.7, marginBottom: 18 }}>{b.texto}</div>
        <div style={{ padding: '14px 18px', background: 'rgba(var(--sky-rgb),0.08)', border: '1px solid rgba(var(--sky-rgb),0.25)', borderRadius: 10, fontSize: 13, lineHeight: 1.7 }}>
          Todavía no está incluido en tu plan{planNombre ? <> <b>{planNombre}</b></> : null}.
          Escríbele al equipo de <b>Corredata</b> y lo activamos para que le saques provecho.
        </div>
      </div>
    );
  }

  if (modulo && !canView(modulo)) return (
    <div style={{ padding: 40, textAlign: 'center', color: 'var(--silver)' }}>
      <div style={{ fontSize: 48, marginBottom: 16 }}>🔒</div>
      <div style={{ fontSize: 20, fontWeight: 700, color: 'var(--text)', marginBottom: 8 }}>Acceso restringido</div>
      <div>No tienes permisos para ver este módulo.</div>
    </div>
  );
  return children;
}

function AppLayout() {
  const { user, loading, marca, licencia } = useApp();
  const [collapsed, setCollapsed] = useState(false);

  // Título de la pestaña del navegador según la marca.
  useEffect(() => {
    if (marca?.nombre) document.title = marca.nombre;
  }, [marca]);

  if (loading) {
    return (
      <div className="loading-screen">
        <div style={{ marginBottom: 16 }}>
          <LogoMarca size={48} marca={marca} />
        </div>
        <NombreMarca marca={marca} fontSize={20} />
        <div className="loading-dots">
          <div className="loading-dot" />
          <div className="loading-dot" />
          <div className="loading-dot" />
        </div>
      </div>
    );
  }

  if (!user) {
    return (
      <Routes>
        <Route path="/login" element={<Login />} />
        <Route path="*" element={<Navigate to="/login" replace />} />
      </Routes>
    );
  }

  // Licencia vencida y gracia cumplida: no se entra (el usuario de soporte de Corredata sí,
  // para poder ayudar a reactivar).
  if (licencia?.bloqueada && !user.es_soporte) {
    return <LicenciaBloqueada />;
  }

  // Contraseña temporal (usuario nuevo o reseteado por el master): no entra al
  // sistema hasta definir una propia. Bloquea todas las rutas.
  if (user.debe_cambiar_password) {
    return <CambioObligatorio />;
  }

  const getTitle = () => {
    const path = window.location.pathname;
    return PAGE_TITLES[path] || marca?.nombre || 'Corredata';
  };

  return (
    <div className="app-layout">
      <Sidebar collapsed={collapsed} onToggle={() => setCollapsed(c => !c)} />
      <div className={`main-content ${collapsed ? 'collapsed' : ''}`}>
        <Header title={getTitle()} />
        <LicenciaAviso />
        <div className="page-wrapper">
          <Routes>
            <Route path="/" element={<ProtectedRoute modulo="dashboard"><Dashboard /></ProtectedRoute>} />
            <Route path="/clientes" element={<ProtectedRoute modulo="clientes"><Clientes /></ProtectedRoute>} />
            <Route path="/polizas" element={<ProtectedRoute modulo="polizas"><Polizas /></ProtectedRoute>} />
            <Route path="/pagos" element={<Navigate to="/cobros" replace />} />
            <Route path="/cobros" element={<ProtectedRoute modulo="cobros"><Cobros /></ProtectedRoute>} />
            <Route path="/comisiones" element={<ProtectedRoute modulo="comisiones"><Comisiones /></ProtectedRoute>} />
            <Route path="/contabilidad" element={<ProtectedRoute modulo="contabilidad"><Contabilidad /></ProtectedRoute>} />
            <Route path="/crm" element={<ProtectedRoute modulo="crm"><CRM /></ProtectedRoute>} />
            <Route path="/reclamos" element={<ProtectedRoute modulo="reclamos"><Reclamos /></ProtectedRoute>} />
            <Route path="/agentes" element={<ProtectedRoute modulo="agentes"><Agentes /></ProtectedRoute>} />
            <Route path="/archivos" element={<ProtectedRoute modulo="archivos"><Archivos /></ProtectedRoute>} />
            <Route path="/reportes" element={<ProtectedRoute modulo="reportes"><Reportes /></ProtectedRoute>} />
            <Route path="/solicitudes" element={<ProtectedRoute modulo="solicitudes"><Solicitudes /></ProtectedRoute>} />
            <Route path="/admin" element={<ProtectedRoute modulo="admin"><Admin /></ProtectedRoute>} />
            <Route path="/login" element={<Navigate to="/" replace />} />
            <Route path="*" element={<Navigate to="/" replace />} />
          </Routes>
        </div>
      </div>
      <AsistenteIA />
      <OnboardingTour />
    </div>
  );
}

export default function App() {
  return (
    <AppProvider>
      <GlobalBusy />
      <BrowserRouter>
        <AppLayout />
      </BrowserRouter>
    </AppProvider>
  );
}
